import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Radio,
  Checkbox,
  FormControlLabel,
  Paper,
  RadioGroup,
  Avatar,
  Chip,
  Container,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  ErrorOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CheckCircle,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { CartContext } from "../../comp/CartContext";
import { WithRouter } from "../../comp/WithRouter";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/* --------------------------- Galeria de Imagens --------------------------- */

function ProductGallery({ images, alt, isMobile }) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: isMobile ? 260 : 380,
          bgcolor: "#f1f5f9",
          borderRadius: isMobile ? 0 : 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#94a3b8",
          fontWeight: 600,
        }}
      >
        Sem imagem disponível
      </Box>
    );
  }

  const active = images[activeIndex];

  return (
    <Box sx={{ width: "100%", bgcolor: "#fff", borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}>
      <Box
        sx={{
          width: "100%",
          height: isMobile ? 280 : 400,
          bgcolor: "#fafafa",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: isMobile ? 0 : 2,
        }}
      >
        <img
          src={`/api/store/photo/${active.name}`}
          alt={alt}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>

      {images.length > 1 && (
        <Box sx={{ display: "flex", gap: 1.5, p: 2, overflowX: "auto", bgcolor: "#fff", borderTop: "1px solid #f1f5f9" }}>
          {images.map((img, index) => (
            <Box
              key={img.name || index}
              onClick={() => setActiveIndex(index)}
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 2.5,
                overflow: "hidden",
                cursor: "pointer",
                border: index === activeIndex ? "2px solid #1976d2" : "1px solid #e2e8f0",
                transition: "all 0.2s ease",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <img
                src={`/api/store/photo/${img.name}`}
                alt={`${alt} - ${index + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/* --------------------------- Descrição do Produto --------------------------- */

function ExpandableDescription({ text, limit = 120 }) {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const isLongText = text.length > limit;
  const displayText = expanded || !isLongText ? text : `${text.substring(0, limit)}...`;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" sx={{ color: "#475569", whiteSpace: "pre-line", lineHeight: 1.6, fontSize: "0.9rem" }}>
        {displayText}
      </Typography>
      {isLongText && (
        <Button
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          endIcon={expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          sx={{ textTransform: "none", p: 0, mt: 0.5, fontWeight: 700, fontSize: "0.825rem" }}
        >
          {expanded ? "Ver menos" : "Ver mais"}
        </Button>
      )}
    </Box>
  );
}

/* ----------------------------- Grupo de Variantes ---------------------------- */

function VariantGroup({ group, selectedIds = [], onToggle }) {
  const items = group.itens || [];
  const min = group.min || 0;
  const max = group.max || 1;
  const isMultiple = max > 1;

  const currentCount = selectedIds.length;
  const isSatisfied = currentCount >= min && (max ? currentCount <= max : true);
  const reachedMax = isMultiple && max > 0 && currentCount >= max;

  const getHelperBadge = () => {
    if (min > 0) {
      if (currentCount < min) {
        return { label: `Obrigatório (Escolha min. ${min})`, color: "warning" };
      }
    }
    if (isSatisfied && currentCount > 0) {
      return { label: "Concluído", color: "success", icon: <CheckCircle style={{ fontSize: 14 }} /> };
    }
    if (max > 1) {
      return { label: `Escolha até ${max}`, color: "default" };
    }
    return { label: "Opcional", color: "default" };
  };

  const badgeInfo = getHelperBadge();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mt: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: !isSatisfied && min > 0 ? "warning.light" : "#e2e8f0",
        bgcolor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>
            {group.nome}
          </Typography>
          {group.descricao && (
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              {group.descricao}
            </Typography>
          )}
        </Box>
        <Chip
          icon={badgeInfo.icon}
          label={badgeInfo.label}
          size="small"
          color={badgeInfo.color}
          variant={badgeInfo.color === "default" ? "outlined" : "filled"}
          sx={{ fontSize: 11, fontWeight: 700 }}
        />
      </Box>

      {isMultiple ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {items.map((v) => {
            const checked = selectedIds.includes(v.id);
            const disabled = !checked && reachedMax;

            return (
              <Box
                key={v.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.8,
                  px: 1.5,
                  borderRadius: 2,
                  bgcolor: checked ? "#f1f5f9" : "transparent",
                  transition: "background-color 0.2s",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onToggle(group, v, true)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: checked ? 700 : 500, color: "#334155" }}>
                      {v.nome}
                    </Typography>
                  }
                  sx={{ flexGrow: 1, m: 0 }}
                />
                {Number(v.preco) > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#475569" }}>
                    +{formatCurrency(v.preco)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <RadioGroup
          value={selectedIds[0] ?? ""}
          onChange={(e) => {
            const variant = items.find((v) => String(v.id) === e.target.value);
            if (variant) onToggle(group, variant, false);
          }}
        >
          {items.map((v) => {
            const checked = selectedIds[0] === v.id;
            return (
              <Box
                key={v.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.8,
                  px: 1.5,
                  borderRadius: 2,
                  bgcolor: checked ? "#f1f5f9" : "transparent",
                }}
              >
                <FormControlLabel
                  value={String(v.id)}
                  control={<Radio />}
                  label={
                    <Typography variant="body2" sx={{ fontWeight: checked ? 700 : 500, color: "#334155" }}>
                      {v.nome}
                    </Typography>
                  }
                  sx={{ flexGrow: 1, m: 0 }}
                />
                {Number(v.preco) > 0 && (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#475569" }}>
                    +{formatCurrency(v.preco)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </RadioGroup>
      )}
    </Paper>
  );
}

/* -------------------------------- Página -------------------------------- */

function ProductDelivery({ params, navigate }) {
  const { uuid } = params;
  const context = useContext(CartContext);
  const store = context?.data || {};
  const themeColor = store.color || "#1976d2";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [adding, setAdding] = useState(false);

  const fetchProduct = useCallback(() => {
    if (!uuid) {
      setError("Produto não encontrado.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetch(`/api/store/product/${uuid}`)
      .then((res) => {
        if (!res.ok) throw new Error("request-failed");
        return res.json();
      })
      .then((data) => {
        setProduct(data);
        setQuantity(1);
        setSelectedVariants({});
        setLoading(false);
      })
      .catch(() => {
        setError("Erro ao carregar dados do produto.");
        setLoading(false);
      });
  }, [uuid]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Gestão de Estoque
  const stock = product?.quantidade;
  const hasStockControl = stock !== null && stock !== undefined;
  const outOfStock = hasStockControl && stock <= 0;
  const maxQuantity = hasStockControl ? Math.max(stock, 0) : Infinity;

  const variantGroups = product?.variant_groups || [];

  // Validação em tempo real do primeiro grupo pendente/obrigatório
  const validationErrorGroup = useMemo(() => {
    return variantGroups.find((group) => {
      const min = group.min || 0;
      const selectedCount = (selectedVariants[group.id] || []).length;
      return selectedCount < min;
    });
  }, [variantGroups, selectedVariants]);

  // Cálculo total
  const totalPrice = useMemo(() => {
    if (!product) return 0;
    let extras = 0;
    variantGroups.forEach((group) => {
      const selectedIds = selectedVariants[group.id] || [];
      (group.itens || []).forEach((v) => {
        if (selectedIds.includes(v.id)) extras += Number(v.preco) || 0;
      });
    });
    return (Number(product.preco) + extras) * quantity;
  }, [product, variantGroups, selectedVariants, quantity]);

  const handleVariantToggle = useCallback((group, variant, isMultiple) => {
    setSelectedVariants((prev) => {
      const current = prev[group.id] || [];
      let next;

      if (isMultiple) {
        next = current.includes(variant.id)
          ? current.filter((id) => id !== variant.id)
          : [...current, variant.id];
      } else {
        next = [variant.id];
      }

      return { ...prev, [group.id]: next };
    });
  }, []);

  const handleDecreaseQuantity = () => setQuantity((q) => Math.max(1, q - 1));
  const handleIncreaseQuantity = () =>
    setQuantity((q) => (hasStockControl ? Math.min(maxQuantity, q + 1) : q + 1));

  const handleAddToCart = async () => {
    if (outOfStock) {
      toast.warn("Este produto está esgotado.");
      return;
    }

    if (validationErrorGroup) {
      toast.warn(`Por favor, selecione as opções do grupo: ${validationErrorGroup.nome}`);
      return;
    }

    if (adding) return;

    setAdding(true);
    try {
      const res = await fetch(`/api/store/product/${product.uuid}/addToCart`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          quantity,
          options: selectedVariants,
        }),
      });

      if (res.status === 401) {
        context.DialogLogin();
        return;
      }

      const data = await res.json().catch(() => null);
      if (data && data.success === false) {
        toast.error(data.error || "Não foi possível adicionar o produto.");
        return;
      }

      context.adicionarAoCarrinho({
        uuid: product.uuid,
        nome_momento: product.nome,
        preco_unitario_momento: product.preco,
        quantidade: quantity,
      });
      toast.success("Produto adicionado ao carrinho!");
      navigate("/");
    } catch (err) {
      console.error(err);
      toast.error("Erro de conexão ao adicionar o produto.");
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
        <CircularProgress sx={{ color: themeColor }} />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          minHeight: "80vh",
          px: 3,
          textAlign: "center",
        }}
      >
        <ErrorOutline sx={{ fontSize: 56, color: "#94a3b8" }} />
        <Typography color="text.secondary" variant="h6">
          {error || "Produto não encontrado."}
        </Typography>
        <Button variant="outlined" onClick={fetchProduct} sx={{ borderRadius: 3, textTransform: "none" }}>
          Tentar novamente
        </Button>
      </Box>
    );
  }

  const images = product.image_list || [];
  const disableAddToCart = adding || outOfStock || Boolean(validationErrorGroup);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#f8fafc", pb: isMobile ? 14 : 6 }}>
      {/* Target App Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: "#ffffff",
          px: { xs: 2, sm: 4 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.02)",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }}
          onClick={() => navigate("/")}
        >
          <Avatar
            src={store.logo ? `/api/store/photo/${store.logo}` : undefined}
            sx={{ width: 38, height: 38, border: "1px solid #e2e8f0" }}
          >
            {store.name?.[0]}
          </Avatar>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {store.name}
          </Typography>
        </Box>
        <IconButton onClick={() => navigate(-1)} size="small" sx={{ bgcolor: "#f1f5f9" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Conteúdo Principal Responsivo */}
      <Container maxWidth="lg" sx={{ pt: { xs: 0, md: 4 }, px: { xs: 0, md: 3 } }}>
        <Grid container spacing={isMobile ? 0 : 4}>
          {/* Coluna 1: Galeria de Imagens */}
          <Grid item size={{ xs: 12, md: 6 }} sx={{ position: { md: "sticky" }, top: { md: 90 }, alignSelf: "start" }}>
            <ProductGallery images={images} alt={product.nome} isMobile={isMobile} />
          </Grid>

          {/* Coluna 2: Informações do Produto e Opções */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                p: { xs: 2.5, md: 3.5 },
                bgcolor: "#ffffff",
                borderRadius: isMobile ? 0 : 4,
                border: isMobile ? "none" : "1px solid #e2e8f0",
                boxShadow: isMobile ? "none" : "0 4px 20px rgba(0,0,0,0.02)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", fontSize: { xs: "1.35rem", md: "1.6rem" } }}>
                  {product.nome}
                </Typography>
                {outOfStock && <Chip label="Esgotado" color="error" size="small" sx={{ fontWeight: 700 }} />}
              </Box>

              <Typography variant="h5" sx={{ mt: 1, fontWeight: 800, color: themeColor }}>
                {formatCurrency(product.preco)}
              </Typography>

              {/* Descrição Expansível */}
              <ExpandableDescription text={product.descricao} limit={140} />

              {/* Alerta de Estoque Baixo */}
              {hasStockControl && !outOfStock && stock <= 5 && (
                <Typography variant="caption" sx={{ color: "#ef4444", fontWeight: 700, mt: 1.5, display: "block" }}>
                  🔥 Restam apenas {stock} unidade{stock > 1 ? "s" : ""} disponíveis!
                </Typography>
              )}

              {/* Grupos de Opções / Variantes */}
              <Box sx={{ mt: 2 }}>
                {variantGroups.map((group) => (
                  <VariantGroup
                    key={group.id}
                    group={group}
                    selectedIds={selectedVariants[group.id] || []}
                    onToggle={handleVariantToggle}
                  />
                ))}
              </Box>

              {/* Seção de Ação Desktop embutida no Container */}
              {!isMobile && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #cbd5e1",
                      borderRadius: 3,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <IconButton onClick={handleDecreaseQuantity} disabled={quantity <= 1 || outOfStock} size="small" sx={{ p: 1.25 }}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ px: 2, fontWeight: 800, minWidth: 24, textAlign: "center" }}>
                      {quantity}
                    </Typography>
                    <IconButton onClick={handleIncreaseQuantity} disabled={(hasStockControl && quantity >= maxQuantity) || outOfStock} size="small" sx={{ p: 1.25 }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    disabled={disableAddToCart}
                    sx={{
                      bgcolor: themeColor,
                      "&:hover": { bgcolor: themeColor, filter: "brightness(0.92)" },
                      py: 1.75,
                      borderRadius: 3,
                      fontWeight: 800,
                      textTransform: "none",
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 3,
                      boxShadow: "none",
                    }}
                    onClick={handleAddToCart}
                  >
                    {adding ? (
                      <CircularProgress size={24} sx={{ color: "white", mx: "auto" }} />
                    ) : outOfStock ? (
                      <span style={{ margin: "0 auto" }}>Produto Esgotado</span>
                    ) : validationErrorGroup ? (
                      <span style={{ margin: "0 auto", fontSize: "0.9rem" }}>
                        Selecione as opções ({validationErrorGroup.nome})
                      </span>
                    ) : (
                      <>
                        <span>Adicionar ao Carrinho</span>
                        <span>{formatCurrency(totalPrice)}</span>
                      </>
                    )}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Rodapé Fixo / Sticky Action Bar exclusivo para Mobile */}
      {isMobile && (
        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            zIndex: 1000,
            bgcolor: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #cbd5e1",
              borderRadius: 3,
              bgcolor: "#f8fafc",
            }}
          >
            <IconButton onClick={handleDecreaseQuantity} disabled={quantity <= 1 || outOfStock} size="small" sx={{ p: 1 }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ px: 1.5, fontWeight: 700, minWidth: 20, textAlign: "center" }}>
              {quantity}
            </Typography>
            <IconButton onClick={handleIncreaseQuantity} disabled={(hasStockControl && quantity >= maxQuantity) || outOfStock} size="small" sx={{ p: 1 }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          <Button
            variant="contained"
            fullWidth
            disabled={disableAddToCart}
            sx={{
              bgcolor: themeColor,
              "&:hover": { bgcolor: themeColor, filter: "brightness(0.9)" },
              py: 1.5,
              borderRadius: 3,
              fontWeight: 700,
              textTransform: "none",
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              boxShadow: "none",
            }}
            onClick={handleAddToCart}
          >
            {adding ? (
              <CircularProgress size={24} sx={{ color: "white", mx: "auto" }} />
            ) : outOfStock ? (
              <span style={{ margin: "0 auto" }}>Produto Esgotado</span>
            ) : validationErrorGroup ? (
              <span style={{ margin: "0 auto", fontSize: "0.875rem" }}>
                Selecione as opções ({validationErrorGroup.nome})
              </span>
            ) : (
              <>
                <span>Adicionar</span>
                <span>{formatCurrency(totalPrice)}</span>
              </>
            )}
          </Button>
        </Paper>
      )}
    </Box>
  );
}

export default WithRouter(ProductDelivery);